"use client";

import Link from "next/link";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { NewsletterForm } from "./NewsletterForm";
import { ReviewPrompt } from "./ReviewPrompt";
import { BrandLogo } from "./BrandLogo";
import { PartnerBadges } from "./PartnerBadges";
import { ADDRESS_LINE, COMPANY, OPENING_HOURS } from "@/lib/facts";
import { filterByVisibleSlugs } from "@/lib/services/catalog";

const footerLinks = {
  services: [
    { name: "Website-Entwicklung", href: "/dienstleistungen/webdesign" },
    { name: "Suchmaschinen-Optimierung", href: "/dienstleistungen/seo" },
    { name: "Social Media Marketing", href: "/dienstleistungen/social-media-marketing" },
    { name: "Markenidentität", href: "/dienstleistungen/branding" },
    { name: "Motion Design & Erklärvideos", href: "/dienstleistungen/motion-design" },
    { name: "Software & App-Entwicklung", href: "/dienstleistungen/software-entwicklung" },
    { name: "Online-Werbung", href: "/dienstleistungen/paid-ads" },
    { name: "Wartung & Hosting", href: "/dienstleistungen/wartungsservice" },
    { name: "Domain-Verwaltung", href: "/dienstleistungen/domains" },
    { name: "KI-Schulungen", href: "/dienstleistungen/ki-schulungen" },
    { name: "KI-Potenzialanalyse", href: "/dienstleistungen/ki-analyse" },
  ],
  regions: [
    { name: "Webdesign Bad Neustadt", href: "/webdesign-bad-neustadt" },
    { name: "SEO Bad Neustadt", href: "/seo-bad-neustadt" },
    { name: "Webdesign Schweinfurt", href: "/webdesign-schweinfurt" },
    { name: "SEO Schweinfurt", href: "/seo-schweinfurt" },
    { name: "Webdesign Würzburg", href: "/webdesign-wuerzburg" },
    { name: "SEO Würzburg", href: "/seo-wuerzburg" },
    { name: "Förderung Digitalisierung", href: "/foerderung" },
  ],
  company: [
    { name: "Über uns", href: "/ueber-uns" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Blog", href: "/blog" },
    { name: "Karriere", href: "/karriere" },
    { name: "Kontakt", href: "/kontakt" },
    { name: "Kostenlose Beratung", href: "/beratung" },
    { name: "Impressum", href: "/impressum" },
    { name: "Datenschutz", href: "/datenschutz" },
    { name: "Barrierefreiheit", href: "/barrierefreiheit" },
    { name: "AGB", href: "/agb" },
  ],
};

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/dmd_studio_gmbh/",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/dmd-studio-gmbh",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@dmd_studio_gmbh",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/people/DMD-Studio-GmbH/61579523897431/",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
];

export function Footer({
  visibleServiceSlugs,
}: {
  /** Öffentlich sichtbare Dienstleistungen (Admin → Dienstleistungen). */
  visibleServiceSlugs?: string[];
} = {}) {
  const visibleServices = filterByVisibleSlugs(footerLinks.services, visibleServiceSlugs);
  // theme wurde hier nie verwendet – Aufruf entfernt, damit useTheme()
  // nicht unnötig im Tree liegt und keine Hydration-Mismatch-Quelle ist.
  return (
    <footer className="border-t" style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)" }}>
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
        <div className="grid gap-8 lg:grid-cols-6 lg:gap-12">
          {/* Brand — 2 cols */}
          <div className="lg:col-span-2">
            <Link href="/" className="inline-block">
              <BrandLogo width={120} height={36} className="h-9 w-auto" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              Design, Marketing und Development aus einer Hand.
              Full-Service Digitalagentur aus Unterfranken.
            </p>

            {/* Contact Info */}
            <div className="mt-5 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--text-quaternary)" }} />
                <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
                  {ADDRESS_LINE}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0" style={{ color: "var(--text-quaternary)" }} />
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  className="text-sm transition-colors hover:text-brand-500"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {COMPANY.phoneInternational}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0" style={{ color: "var(--text-quaternary)" }} />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-sm transition-colors hover:text-brand-500"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {COMPANY.email}
                </a>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="mt-5 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border transition-all duration-150 hover:border-brand-300 hover:text-brand-500"
                  style={{
                    color: "var(--text-quaternary)",
                    borderColor: "var(--border)",
                    backgroundColor: "var(--bg)",
                  }}
                  aria-label={`${social.name} von DMD Studio`}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            {/* Review-Hinweis (erscheint nur wenn Review-Link konfiguriert) */}
            <div className="mt-5">
              <ReviewPrompt variant="inline" />
            </div>

            {/* Partner-/Vertrauens-Badge */}
            <div className="mt-6">
              <p
                className="mb-2 text-xs font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-quaternary)" }}
              >
                Offizieller Partner
              </p>
              <PartnerBadges />
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              Leistungen
            </h3>
            <ul className="mt-4 space-y-3">
              {visibleServices.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm transition-colors duration-100 hover:text-brand-500"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="mt-8 text-sm font-semibold" style={{ color: "var(--text)" }}>
              Regional &amp; Förderung
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.regions.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm transition-colors duration-100 hover:text-brand-500"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              Unternehmen
            </h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm transition-colors duration-100 hover:text-brand-500"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt & Öffnungszeiten + Newsletter — 2 cols */}
          <div className="lg:col-span-2">
            {/* Öffnungszeiten */}
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              Öffnungszeiten
            </h3>
            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--text-quaternary)" }} />
                <div className="text-sm" style={{ color: "var(--text-tertiary)" }}>
                  {OPENING_HOURS.display.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  <p className="mt-1 text-xs" style={{ color: "var(--text-quaternary)" }}>
                    {OPENING_HOURS.closedNote}
                  </p>
                </div>
              </div>
              <div
                className="ml-6.5 rounded-md border px-3 py-2"
                style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
              >
                <p className="text-xs font-semibold text-brand-500">24/7 Support</p>
                <p className="mt-0.5 text-xs" style={{ color: "var(--text-quaternary)" }}>
                  Für Bestandskunden auch am Wochenende erreichbar (mit Wartezeiten).
                </p>
              </div>
            </div>

            {/* Newsletter */}
            <h3 className="mt-6 text-sm font-semibold" style={{ color: "var(--text)" }}>
              Newsletter
            </h3>
            <p className="mt-2 text-sm" style={{ color: "var(--text-tertiary)" }}>
              Bleiben Sie auf dem Laufenden mit Tipps und Neuigkeiten.
            </p>
            <div className="mt-3">
              <NewsletterForm />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="mt-12 flex flex-col items-center justify-between gap-4 border-t pt-8 sm:flex-row"
          style={{ borderColor: "var(--border)" }}
        >
          <p className="text-sm" style={{ color: "var(--text-quaternary)" }}>
            &copy;{" "}
            <span suppressHydrationWarning>{new Date().getFullYear()}</span>{" "}
            DMD Studio GmbH. Alle Rechte vorbehalten.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link href="/impressum" className="text-sm transition-colors hover:text-brand-500" style={{ color: "var(--text-quaternary)" }}>
              Impressum
            </Link>
            <Link href="/datenschutz" className="text-sm transition-colors hover:text-brand-500" style={{ color: "var(--text-quaternary)" }}>
              Datenschutz
            </Link>
            <Link href="/barrierefreiheit" className="text-sm transition-colors hover:text-brand-500" style={{ color: "var(--text-quaternary)" }}>
              Barrierefreiheit
            </Link>
            <Link href="/agb" className="text-sm transition-colors hover:text-brand-500" style={{ color: "var(--text-quaternary)" }}>
              AGB
            </Link>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  // Triggert das Re-Open-Event im CookieBanner – behält die
                  // bisherige Auswahl bei und ermöglicht Anpassen.
                  window.dispatchEvent(new CustomEvent("cookie-banner:reopen"));
                }
              }}
              className="text-sm transition-colors hover:text-brand-500"
              style={{ color: "var(--text-quaternary)" }}
            >
              Cookie-Einstellungen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
